import { CONSTITUTION_TYPES } from '../../config/constitutionTypes';

const ConstitutionCard = ({
    constitution,
    compact = false
}) => {
    if (!constitution?.primary) {
        return (
            <div className="constitution-card empty" style={styles.emptyCard}>
                <span style={styles.emptyText}>Chưa có dữ liệu thể chất</span>
            </div>
        );
    }

    const { primary, secondary = [], status, details } = constitution;

    // Helper to get full constitution info from type/id
    const getConstitutionInfo = (type) => {
        return CONSTITUTION_TYPES[type] || null;
    };

    const statusColors = {
        balanced: '#10B981',
        possibly_balanced: '#34D399',
        deviation: '#F59E0B',
        possible_deviation: '#FFC107',
        unclear: '#9E9E9E'
    };

    const statusLabels = {
        balanced: 'Cân bằng',
        possibly_balanced: 'Có xu hướng cân bằng',
        deviation: 'Có thiên lệch',
        possible_deviation: 'Có thể thiên lệch',
        unclear: 'Chưa rõ ràng'
    };

    if (compact) {
        return (
            <div className="constitution-card compact" style={styles.compactCard}>
                <div style={styles.iconLarge}>{primary.icon}</div>
                <div style={styles.compactInfo}>
                    <div style={styles.compactName}>{primary.name_vi}</div>
                    <div style={styles.compactCn}>{primary.name_cn}</div>
                </div>
                <div style={{
                    ...styles.scoreBadge,
                    backgroundColor: primary.color || '#673AB7'
                }}>
                    {primary.score}%
                </div>
            </div>
        );
    }

    return (
        <div className="constitution-card" style={styles.card}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.iconContainer}>
                    <span style={styles.iconLarge}>{primary.icon}</span>
                </div>
                <div style={styles.headerInfo}>
                    <h3 style={styles.title}>{primary.name_vi}</h3>
                    <span style={styles.subtitle}>{primary.name_cn} · {primary.name_en}</span>
                </div>
                <div style={{
                    ...styles.scoreCircle,
                    borderColor: primary.color || '#673AB7'
                }}>
                    <span style={styles.scoreValue}>{primary.score}</span>
                    <span style={styles.scoreLabel}>%</span>
                </div>
            </div>

            {/* Status badge */}
            <div style={{
                ...styles.statusBadge,
                backgroundColor: statusColors[status] || '#9E9E9E'
            }}>
                {statusLabels[status] || status}
            </div>

            {/* Secondary constitutions */}
            {secondary.length > 0 && (
                <div style={styles.secondarySection}>
                    <div style={styles.sectionLabel}>Thể chất phụ:</div>
                    <div style={styles.secondaryList}>
                        {secondary.map((sec, idx) => {
                            const info = getConstitutionInfo(sec.type);
                            return (
                                <div key={idx} style={styles.secondaryItem}>
                                    <span style={styles.secondaryIcon}>{info?.icon || '•'}</span>
                                    <span style={styles.secondaryName}>{info?.name || sec.type}</span>
                                    <span style={styles.secondaryScore}>{sec.score}%</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Characteristics */}
            {details?.characteristics && (
                <div style={styles.characteristicsSection}>
                    <div style={styles.sectionLabel}>Đặc điểm:</div>

                    {details.characteristics.physical && (
                        <div style={styles.charGroup}>
                            <span style={styles.charLabel}>🧍 Hình thể:</span>
                            <span style={styles.charValue}>
                                {details.characteristics.physical.slice(0, 2).join(', ')}
                            </span>
                        </div>
                    )}

                    {details.characteristics.physiological && (
                        <div style={styles.charGroup}>
                            <span style={styles.charLabel}>⚡ Sinh lý:</span>
                            <span style={styles.charValue}>
                                {details.characteristics.physiological.slice(0, 2).join(', ')}
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* Tongue & Pulse */}
            {(details?.tongue_signs || details?.pulse_signs) && (
                <div style={styles.signsSection}>
                    {details.tongue_signs && (
                        <div style={styles.signItem}>
                            <span style={styles.signIcon}>👅</span>
                            <span style={styles.signText}>{details.tongue_signs.description}</span>
                        </div>
                    )}
                    {details.pulse_signs && (
                        <div style={styles.signItem}>
                            <span style={styles.signIcon}>💓</span>
                            <span style={styles.signText}>{details.pulse_signs.description}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Disease tendencies */}
            {details?.disease_tendency && (
                <div style={styles.tendencySection}>
                    <div style={styles.sectionLabel}>⚠️ Xu hướng bệnh:</div>
                    <div style={styles.tendencyList}>
                        {details.disease_tendency.slice(0, 4).map((disease, idx) => (
                            <span key={idx} style={styles.tendencyTag}>
                                {disease}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Treatment principle */}
            {details?.treatment_principle && (
                <div style={styles.principleSection}>
                    <div style={styles.sectionLabel}>💊 Nguyên tắc điều trị:</div>
                    <div style={styles.principleText}>{details.treatment_principle}</div>
                </div>
            )}
        </div>
    );
};

const styles = {
    card: {
        background: 'var(--bg-secondary, #16213e)',
        borderRadius: '16px',
        padding: '20px',
        border: '1px solid var(--border-color, #374151)'
    },
    compactCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        background: 'var(--bg-secondary, #16213e)',
        borderRadius: '12px',
        padding: '12px 16px',
        border: '1px solid var(--border-color, #374151)'
    },
    emptyCard: {
        background: 'var(--bg-secondary, #16213e)',
        borderRadius: '12px',
        padding: '20px',
        textAlign: 'center'
    },
    emptyText: {
        color: 'var(--text-muted, #9CA3AF)',
        fontSize: '14px'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '12px'
    },
    iconContainer: {
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        background: 'var(--bg-tertiary, #1a1a2e)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    iconLarge: {
        fontSize: '28px'
    },
    headerInfo: {
        flex: 1
    },
    title: {
        margin: 0,
        fontSize: '18px',
        fontWeight: '600',
        color: 'var(--text-color, #fff)'
    },
    subtitle: {
        fontSize: '12px',
        color: 'var(--text-muted, #9CA3AF)'
    },
    scoreCircle: {
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        border: '3px solid',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-tertiary, #1a1a2e)'
    },
    scoreValue: {
        fontSize: '18px',
        fontWeight: '700',
        color: 'var(--text-color, #fff)',
        lineHeight: 1
    },
    scoreLabel: {
        fontSize: '10px',
        color: 'var(--text-muted, #9CA3AF)'
    },
    scoreBadge: {
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#fff'
    },
    compactInfo: {
        flex: 1
    },
    compactName: {
        fontSize: '15px',
        fontWeight: '600',
        color: 'var(--text-color, #fff)'
    },
    compactCn: {
        fontSize: '12px',
        color: 'var(--text-muted, #9CA3AF)'
    },
    statusBadge: {
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '500',
        color: '#fff',
        marginBottom: '16px'
    },
    sectionLabel: {
        fontSize: '12px',
        fontWeight: '600',
        color: 'var(--text-muted, #9CA3AF)',
        marginBottom: '8px'
    },
    secondarySection: {
        marginBottom: '16px'
    },
    secondaryList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px'
    },
    secondaryItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px'
    },
    secondaryIcon: {
        fontSize: '16px'
    },
    secondaryName: {
        flex: 1,
        color: 'var(--text-color, #fff)'
    },
    secondaryScore: {
        color: 'var(--text-muted, #9CA3AF)',
        fontSize: '12px'
    },
    characteristicsSection: {
        marginBottom: '12px'
    },
    charGroup: {
        display: 'flex',
        gap: '8px',
        fontSize: '13px',
        marginBottom: '4px'
    },
    charLabel: {
        color: 'var(--text-muted, #9CA3AF)',
        minWidth: '80px'
    },
    charValue: {
        color: 'var(--text-color, #fff)',
        flex: 1
    },
    signsSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        marginBottom: '12px'
    },
    signItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px'
    },
    signIcon: {
        fontSize: '14px'
    },
    signText: {
        color: 'var(--text-color, #fff)'
    },
    tendencySection: {
        marginBottom: '12px'
    },
    tendencyList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px'
    },
    tendencyTag: {
        background: 'rgba(255, 152, 0, 0.2)',
        color: '#FFB74D',
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '11px'
    },
    principleSection: {
        background: 'var(--bg-tertiary, #1a1a2e)',
        borderRadius: '8px',
        padding: '12px'
    },
    principleText: {
        fontSize: '13px',
        color: 'var(--accent-color, #4ECDC4)',
        fontWeight: '500'
    }
};

export default ConstitutionCard;
